import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { motion, useReducedMotion, type HTMLMotionProps, type Variants } from 'framer-motion';
import { cn } from '@/lib/utils';
import { morphSpringSoft } from '@/lib/motion';

export const rdEase = [0.16, 1, 0.3, 1] as const;

export const rdFadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

export const rdFadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export const rdScaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 12 },
  visible: { opacity: 1, scale: 1, y: 0 },
};

export const rdSlideRight: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0 },
};

export const rdSlideLeft: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0 },
};

const defaultViewport = { once: true, margin: '-80px' as const };

interface RdRevealProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  children: ReactNode;
  delay?: number;
  duration?: number;
  variant?: 'fadeUp' | 'fadeIn' | 'scaleIn' | 'slideRight' | 'slideLeft';
  /** Animate on mount instead of when scrolled into view */
  immediate?: boolean;
}

const variantMap = {
  fadeUp: rdFadeUp,
  fadeIn: rdFadeIn,
  scaleIn: rdScaleIn,
  slideRight: rdSlideRight,
  slideLeft: rdSlideLeft,
};

export function RdReveal({
  children,
  className,
  delay = 0,
  duration = 0.55,
  variant = 'fadeUp',
  immediate = false,
  ...rest
}: RdRevealProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      animate={immediate ? 'visible' : undefined}
      whileInView={immediate ? undefined : 'visible'}
      viewport={immediate ? undefined : defaultViewport}
      variants={variantMap[variant]}
      transition={{ duration, delay, ease: rdEase }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

interface RdStaggerProps {
  children: ReactNode;
  className?: string;
  stagger?: number;
  delayChildren?: number;
}

export function RdStagger({
  children,
  className,
  stagger = 0.09,
  delayChildren = 0.04,
}: RdStaggerProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={defaultViewport}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren } },
      }}
    >
      {children}
    </motion.div>
  );
}

interface RdStaggerItemProps {
  children: ReactNode;
  className?: string;
  variant?: keyof typeof variantMap;
}

export function RdStaggerItem({
  children,
  className,
  variant = 'fadeUp',
}: RdStaggerItemProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      variants={variantMap[variant]}
      transition={{ duration: 0.5, ease: rdEase }}
    >
      {children}
    </motion.div>
  );
}

interface RdSectionProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  tight?: boolean;
  /** Hero sections animate immediately on load */
  hero?: boolean;
}

/** Section with scroll-triggered `rd-visible` class for CSS child animations */
export function RdSection({ children, className, style, tight, hero }: RdSectionProps) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(hero ?? false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (hero || reduce) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '-60px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hero, reduce]);

  return (
    <section
      ref={ref as Ref<HTMLElement>}
      style={style}
      className={cn('bloc', tight && 'tight', hero && 'rd-hero', visible && 'rd-visible', className)}
    >
      {children}
    </section>
  );
}

interface RdHeroStaggerProps {
  children: ReactNode;
  className?: string;
}

export function RdHeroStagger({ children, className }: RdHeroStaggerProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } } }}
    >
      {children}
    </motion.div>
  );
}

export function RdHeroItem({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      variants={rdFadeUp}
      transition={{ duration: 0.6, ease: rdEase }}
    >
      {children}
    </motion.div>
  );
}

/** Subtle hover/tap wrapper for buttons and links */
export function RdInteractive({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const reduce = useReducedMotion();

  if (reduce) {
    return <span className={className}>{children}</span>;
  }

  return (
    <motion.span
      className={cn('inline-flex', className)}
      whileHover={{ y: -2, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={morphSpringSoft}
    >
      {children}
    </motion.span>
  );
}
